import React, { useState, useRef } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { uploadService } from '../api/services';
import './BlockRenderer.css';

// Parse content: try JSON blocks first, fallback to plain text
export function parseContent(content) {
  if (!content) return [{ type: 'text', content: '' }];
  try {
    const parsed = JSON.parse(content);
    if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].type) return parsed;
  } catch {}
  return [{ type: 'text', content }];
}

const ImageCarousel = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollRef = useRef(null);

  if (!images || images.length === 0) return null;

  return (
    <div className="image-carousel">
      <div className="carousel-container" ref={scrollRef}>
        <div className="carousel-track" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
          {images.map((img, i) => (
            <div key={i} className="carousel-slide">
              <img src={img.url} alt={img.name || `Image ${i + 1}`} className="carousel-image" />
              {img.name && <p className="carousel-caption">{img.name}</p>}
            </div>
          ))}
        </div>
      </div>
      {images.length > 1 && (
        <>
          <button className="carousel-btn carousel-prev" onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))} disabled={currentIndex === 0}>‹</button>
          <button className="carousel-btn carousel-next" onClick={() => setCurrentIndex(Math.min(images.length - 1, currentIndex + 1))} disabled={currentIndex === images.length - 1}>›</button>
          <div className="carousel-dots">
            {images.map((_, i) => (
              <span key={i} className={`carousel-dot ${i === currentIndex ? 'active' : ''}`} onClick={() => setCurrentIndex(i)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const BlockRenderer = ({ content, attachments = [], searchKeyword = '', codeTheme = 'dark', onThemeChange = null }) => {
  const [localCodeTheme, setLocalCodeTheme] = useState(codeTheme);
  const blocks = parseContent(content);
  const imageAttachments = attachments.filter(a => a.file_type === 'images');
  const otherAttachments = attachments.filter(a => a.file_type !== 'images');

  const handleThemeToggle = () => {
    const newTheme = localCodeTheme === 'dark' ? 'light' : 'dark';
    setLocalCodeTheme(newTheme);
    if (onThemeChange) {
      onThemeChange(newTheme);
    }
  };

  const syntaxStyle = localCodeTheme === 'dark' ? oneDark : oneLight;

  const highlightText = (text) => {
    if (!searchKeyword || !text) return text;
    const regex = new RegExp(`(${searchKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      regex.test(part) ? <mark key={i} className="search-highlight">{part}</mark> : part
    );
  };

  return (
    <div className="block-renderer">
      {blocks.map((block, index) => (
        <div key={index} className={`render-block render-${block.type}`}>
          {block.type === 'code' ? (
            <div className="code-block-wrapper">
              <div className="code-block-header">
                <span className="code-lang-label">{block.language || 'plaintext'}</span>
                <div className="code-block-actions">
                  <button className="code-theme-btn" onClick={handleThemeToggle} title={`切換為${localCodeTheme === 'dark' ? '亮色' : '暗色'}`}>
                    {localCodeTheme === 'dark' ? '☀️' : '🌙'}
                  </button>
                  <button className="code-copy-btn" onClick={() => navigator.clipboard.writeText(block.content)} title="複製">📋</button>
                </div>
              </div>
              <SyntaxHighlighter
                language={block.language || 'plaintext'}
                style={syntaxStyle}
                showLineNumbers
                customStyle={{ margin: 0, borderRadius: '0 0 8px 8px', fontSize: '13px' }}
              >
                {block.content || ''}
              </SyntaxHighlighter>
            </div>
          ) : block.type === 'image' ? (
            <div className="render-image-wrap">
              <img className="render-image" src={uploadService.getFileUrl(block.file_path)} alt={block.alt || ''} />
              {block.alt && <p className="render-image-caption">{block.alt}</p>}
            </div>
          ) : (
            <div className="text-block">
              {block.content?.split('\n').map((line, i) => (
                <p key={i}>{highlightText(line) || <br />}</p>
              ))}
            </div>
          )}
        </div>
      ))}

      {imageAttachments.length > 0 && (
        <ImageCarousel images={imageAttachments.map(a => ({ url: a.url, name: a.original_name }))} />
      )}

      {otherAttachments.length > 0 && (
        <div className="attachments-list">
          <h4>📎 附件</h4>
          {otherAttachments.map(a => (
            <a key={a.id} href={uploadService.getDownloadUrl(a.id)} download className="attachment-item">
              <span className="attachment-icon">{getFileIcon(a.file_type)}</span>
              <span className="attachment-name">{a.original_name}</span>
              <span className="attachment-size">{formatFileSize(a.file_size)}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

function getFileIcon(type) {
  const icons = { documents: '📄', archives: '📦', code: '💻', videos: '🎬', audio: '🎵' };
  return icons[type] || '📁';
}

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default BlockRenderer;
